// ════════════════════════════════════════════════════════════════════════
//  Supabase-Client für die Erwachsenen-Apps
//
//  Wird von `apps/verwaltung` und `apps/admin` benutzt – und bewusst NICHT
//  von `apps/lern-app`: Schüler haben keinen JWT, dort wären ein Auth-SDK und
//  sein Storage-Residuum auf dem Koffer-iPad nur Ballast und Risiko.
// ════════════════════════════════════════════════════════════════════════

import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "../datenbank.gen.js";

export type OttoClient = SupabaseClient<Database>;

export type ClientConfig = {
  readonly url: string;
  readonly anonKey: string;
};

/**
 * Liest die Konfiguration aus den Vite-Umgebungsvariablen und beschwert sich
 * sofort und deutlich, wenn etwas fehlt.
 *
 * Der Grund für das laute Scheitern: ein Client gegen eine leere URL liefert
 * Netzwerkfehler, die wie ein Serverausfall aussehen. Diesen Irrweg hat man
 * einmal und dann nie wieder.
 */
export function configFrom(env: Record<string, string | undefined>): ClientConfig {
  const url = env["VITE_SUPABASE_URL"];
  const anonKey = env["VITE_SUPABASE_ANON_KEY"];
  const missing = [
    ...(url ? [] : ["VITE_SUPABASE_URL"]),
    ...(anonKey ? [] : ["VITE_SUPABASE_ANON_KEY"]),
  ];
  if (url === undefined || anonKey === undefined) {
    throw new Error(
      `Konfiguration fehlt: ${missing.join(", ")}. ` +
        "Für die lokale Entwicklung in .env.local eintragen, Werte aus `npx supabase status`.",
    );
  }
  return { url, anonKey };
}

export function createOttoClient(config: ClientConfig): OttoClient {
  return createClient<Database>(config.url, config.anonKey, {
    auth: {
      // Magic Link kommt als Fragment in der URL zurück.
      detectSessionInUrl: true,
      persistSession: true,
      autoRefreshToken: true,
      flowType: "pkce",
    },
    // KEINE eigenen Kopfzeilen.
    //
    // Hier stand `x-otto-client: verwaltung` – gesetzt, nie gelesen, von keiner
    // Zeile serverseitig ausgewertet. Gekostet hat er trotzdem: eine eigene
    // Kopfzeile macht aus jedem Aufruf an eine Edge Function eine Vorabfrage,
    // die deren CORS-Liste kennen muss. Sie kannte ihn nicht, und im Staging
    // schlug das Einladen einer Lehrkraft mit einem CORS-Fehler ohne Status und
    // ohne Körper fehl. PostgREST und GoTrue spiegeln die genannten Kopfzeilen,
    // deshalb fiel es dort nie auf.
    //
    // Die Functions spiegeln inzwischen auch. Der Kopf bleibt trotzdem weg: was
    // niemand liest, muss auch nicht durch drei CORS-Listen passen.
  });
}
